import React, { useEffect, useMemo, useRef, useState } from "react"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { db } from "../firebase"

const STORAGE_KEY = "branmont-mark-list-data"
const DATA_DOC = doc(db, "marklist", "data")

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`

const pad = (value) => String(value).padStart(2, "0")

const loadData = () => {
	try {
		const saved = window.localStorage.getItem(STORAGE_KEY)
		const parsed = saved ? JSON.parse(saved) : null
		return Array.isArray(parsed) ? parsed : []
	} catch (error) {
		console.error("Could not read saved mark list data:", error)
		return []
	}
}

/* ── Reusable admin list row ── */
const AdminRow = ({
	icon,
	title,
	meta,
	isEditing,
	editValue,
	onOpen,
	onEditStart,
	onEditChange,
	onEditSave,
	onEditCancel,
	onDelete,
}) => (
	<article className={`admin-card ${isEditing ? "is-editing" : ""}`}>
		{isEditing ? (
			<form
				className="admin-card-edit"
				onSubmit={(e) => {
					e.preventDefault()
					onEditSave()
				}}
			>
				<span className="admin-thumb">
					<i className={icon} aria-hidden="true"></i>
				</span>
				<input
					autoFocus
					className="admin-edit-input"
					value={editValue}
					onChange={(e) => onEditChange(e.target.value)}
					aria-label={`Rename ${title}`}
				/>
				<div className="admin-card-actions">
					<button type="submit" className="admin-btn is-primary">
						<i className="ri-check-line" aria-hidden="true"></i> Save
					</button>
					<button type="button" className="admin-btn" onClick={onEditCancel}>
						<i className="ri-close-line" aria-hidden="true"></i> Cancel
					</button>
				</div>
			</form>
		) : (
			<>
				<button
					type="button"
					className="admin-card-main"
					onClick={onOpen}
					disabled={!onOpen}
				>
					<span className="admin-thumb">
						<i className={icon} aria-hidden="true"></i>
					</span>
					<span className="admin-card-text">
						<strong>{title}</strong>
						<span>{meta}</span>
					</span>
					{onOpen && <i className="ri-arrow-right-s-line admin-card-go" aria-hidden="true"></i>}
				</button>
				<div className="admin-card-actions">
					<button type="button" className="admin-btn" onClick={onEditStart}>
						<i className="ri-pencil-line" aria-hidden="true"></i> Edit
					</button>
					<button type="button" className="admin-btn is-danger" onClick={onDelete}>
						<i className="ri-delete-bin-line" aria-hidden="true"></i> Delete
					</button>
				</div>
			</>
		)}
	</article>
)

const MarkListEnter = () => {
	const [classes, setClasses] = useState(loadData)
	const [selectedClassId, setSelectedClassId] = useState("")
	const [selectedCourseId, setSelectedCourseId] = useState("")
	const [selectedStudentId, setSelectedStudentId] = useState("")
	const [selectedExamId, setSelectedExamId] = useState("")

	const [classInput, setClassInput] = useState("")
	const [courseInput, setCourseInput] = useState("")
	const [studentInput, setStudentInput] = useState({ name: "", rollNo: "" })
	const [subjectDraft, setSubjectDraft] = useState({ name: "", total: "100" })
	const [examDraft, setExamDraft] = useState({ name: "", subjects: [] })
	const [notice, setNotice] = useState("")

	const [editId, setEditId] = useState("")
	const [editValue, setEditValue] = useState("")
	const [modalType, setModalType] = useState(null) // "class" | "course" | "student" | "subject" | "exam"
	const [syncStatus, setSyncStatus] = useState("loading") // "loading" | "saving" | "saved" | "error"
	const hydrated = useRef(false)
	const classesRef = useRef(classes)

	const saveToCloud = (next) => {
		setSyncStatus("saving")
		return setDoc(DATA_DOC, { classes: next, updatedAt: Date.now() })
			.then(() => setSyncStatus("saved"))
			.catch((error) => {
				console.error("Could not save mark list to Firestore:", error)
				setSyncStatus("error")
			})
	}

	// Load the register from Firestore once on mount (localStorage is just an instant cache)
	useEffect(() => {
		let active = true
		getDoc(DATA_DOC)
			.then((snap) => {
				if (!active) return
				if (snap.exists() && Array.isArray(snap.data().classes)) {
					// Cloud is the source of truth
					setClasses(snap.data().classes)
					hydrated.current = true
					setSyncStatus("saved")
				} else {
					// No cloud data yet — seed it from anything we already have locally
					hydrated.current = true
					if (classesRef.current.length > 0) {
						saveToCloud(classesRef.current)
					} else {
						setSyncStatus("saved")
					}
				}
			})
			.catch((error) => {
				console.error("Could not load mark list from Firestore:", error)
				if (!active) return
				hydrated.current = true
				setSyncStatus("error")
			})
		return () => {
			active = false
		}
	}, [])

	// Persist to localStorage always, and to Firestore once we've hydrated from it
	useEffect(() => {
		classesRef.current = classes
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(classes))
		} catch (error) {
			console.error("Could not cache mark list data:", error)
		}
		if (!hydrated.current) return
		saveToCloud(classes)
	}, [classes])

	useEffect(() => {
		if (!notice) return undefined
		const timer = setTimeout(() => setNotice(""), 2600)
		return () => clearTimeout(timer)
	}, [notice])

	const selectedClass = useMemo(
		() => classes.find((item) => item.id === selectedClassId) || null,
		[classes, selectedClassId]
	)

	const selectedCourse = useMemo(
		() => selectedClass?.courses.find((item) => item.id === selectedCourseId) || null,
		[selectedClass, selectedCourseId]
	)

	const subjects = useMemo(() => selectedCourse?.subjects || [], [selectedCourse])

	const selectedExam = useMemo(
		() => selectedCourse?.exams?.find((item) => item.id === selectedExamId) || null,
		[selectedCourse, selectedExamId]
	)

	const examTotals = (exam, studentId) => {
		const scores = exam.scores?.[studentId] || {}
		const obtained = exam.subjects.reduce((sum, subj) => sum + Number(scores[subj.id] || 0), 0)
		const maximum = exam.subjects.reduce((sum, subj) => sum + Number(subj.total || 0), 0)
		const percentage = maximum > 0 ? (obtained / maximum) * 100 : 0
		return { obtained, maximum, percentage }
	}

	const cancelEdit = () => {
		setEditId("")
		setEditValue("")
	}

	/* ── Classes ── */
	const addClass = (e) => {
		e.preventDefault()
		const name = classInput.trim()
		if (!name) return

		const newClass = { id: makeId(), name, courses: [] }
		setClasses((prev) => [...prev, newClass])
		setClassInput("")
		setModalType(null)
		setSelectedClassId(newClass.id)
		setSelectedCourseId("")
		setSelectedStudentId("")
		setNotice(`${name} added`)
	}

	const openModal = (type) => {
		if (type === "class") setClassInput("")
		if (type === "course") setCourseInput("")
		if (type === "student") setStudentInput({ name: "", rollNo: "" })
		if (type === "subject") setSubjectDraft({ name: "", total: "100" })
		if (type === "exam")
			setExamDraft({
				name: "",
				subjects: (selectedCourse?.subjects || []).map((s) => ({
					id: makeId(),
					name: s.name,
					total: String(s.total),
				})),
			})
		setModalType(type)
	}

	const closeModal = () => setModalType(null)

	const removeClass = (classId) => {
		setClasses((prev) => prev.filter((item) => item.id !== classId))
		if (selectedClassId === classId) {
			setSelectedClassId("")
			setSelectedCourseId("")
			setSelectedStudentId("")
		}
	}

	const renameClass = () => {
		const name = editValue.trim()
		if (!name) return
		setClasses((prev) => prev.map((item) => (item.id === editId ? { ...item, name } : item)))
		cancelEdit()
	}

	const selectClass = (classId) => {
		setSelectedClassId(classId)
		setSelectedCourseId("")
		setSelectedStudentId("")
		setSelectedExamId("")
		cancelEdit()
	}

	/* ── Courses ── */
	const addCourse = (e) => {
		e.preventDefault()
		const name = courseInput.trim()
		if (!name || !selectedClass) return

		const newCourse = { id: makeId(), name, subjects: [], students: [], exams: [] }
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId ? { ...item, courses: [...item.courses, newCourse] } : item
			)
		)
		setCourseInput("")
		setModalType(null)
		setNotice(`${name} added`)
	}

	const removeCourse = (courseId) => {
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? { ...item, courses: item.courses.filter((course) => course.id !== courseId) }
					: item
			)
		)
		if (selectedCourseId === courseId) {
			setSelectedCourseId("")
			setSelectedStudentId("")
			setSelectedExamId("")
		}
	}

	const renameCourse = () => {
		const name = editValue.trim()
		if (!name) return
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === editId ? { ...course, name } : course
							),
					  }
					: item
			)
		)
		cancelEdit()
	}

	const selectCourse = (courseId) => {
		setSelectedCourseId(courseId)
		setSelectedStudentId("")
		cancelEdit()
	}

	/* ── Students ── */
	const addStudent = (e) => {
		e.preventDefault()
		const name = studentInput.name.trim()
		if (!name || !selectedCourse) return

		const newStudent = { id: makeId(), name, rollNo: studentInput.rollNo.trim(), marks: [] }
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId
									? { ...course, students: [...course.students, newStudent] }
									: course
							),
					  }
					: item
			)
		)
		setStudentInput({ name: "", rollNo: "" })
		setModalType(null)
		setNotice(`${name} added`)
	}

	const removeStudent = (studentId) => {
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId
									? { ...course, students: course.students.filter((s) => s.id !== studentId) }
									: course
							),
					  }
					: item
			)
		)
		if (selectedStudentId === studentId) setSelectedStudentId("")
	}

	const renameStudent = () => {
		const name = editValue.trim()
		if (!name) return
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId
									? {
											...course,
											students: course.students.map((student) =>
												student.id === editId ? { ...student, name } : student
											),
									  }
									: course
							),
					  }
					: item
			)
		)
		cancelEdit()
	}

	const selectExam = (examId) => {
		setSelectedExamId(examId)
		cancelEdit()
	}

	/* ── Exam draft (subjects builder inside the add-exam popup) ── */
	const addExamSubjectRow = () =>
		setExamDraft((prev) => ({
			...prev,
			subjects: [...prev.subjects, { id: makeId(), name: "", total: "100" }],
		}))

	const updateExamSubjectRow = (id, key, val) =>
		setExamDraft((prev) => ({
			...prev,
			subjects: prev.subjects.map((s) => (s.id === id ? { ...s, [key]: val } : s)),
		}))

	const removeExamSubjectRow = (id) =>
		setExamDraft((prev) => ({
			...prev,
			subjects: prev.subjects.filter((s) => s.id !== id),
		}))

	/* ── Subjects (per course) ── */
	const addSubject = (e) => {
		e.preventDefault()
		const name = subjectDraft.name.trim()
		if (!name || !selectedCourse) return

		const newSubject = {
			id: makeId(),
			name,
			total: subjectDraft.total === "" ? 100 : Number(subjectDraft.total),
		}
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId
									? { ...course, subjects: [...(course.subjects || []), newSubject] }
									: course
							),
					  }
					: item
			)
		)
		setSubjectDraft({ name: "", total: "100" })
		setModalType(null)
		setNotice(`${name} added`)
	}

	const removeSubject = (subjectId) => {
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId
									? {
											...course,
											subjects: (course.subjects || []).filter((s) => s.id !== subjectId),
											students: course.students.map((student) => {
												if (!student.scores || !(subjectId in student.scores)) return student
												const nextScores = { ...student.scores }
												delete nextScores[subjectId]
												return { ...student, scores: nextScores }
											}),
									  }
									: course
							),
					  }
					: item
			)
		)
	}

	/* ── Exams (per course) ── */
	const updateCourse = (updater) => {
		setClasses((prev) =>
			prev.map((item) =>
				item.id === selectedClassId
					? {
							...item,
							courses: item.courses.map((course) =>
								course.id === selectedCourseId ? updater(course) : course
							),
					  }
					: item
			)
		)
	}

	const addExam = (e) => {
		e.preventDefault()
		const name = examDraft.name.trim()
		if (!name || !selectedCourse) return

		const subs = examDraft.subjects
			.filter((s) => s.name.trim())
			.map((s) => ({
				id: makeId(),
				name: s.name.trim(),
				total: s.total === "" ? 100 : Number(s.total),
			}))
		const newExam = { id: makeId(), name, subjects: subs, scores: {} }
		updateCourse((course) => ({ ...course, exams: [...(course.exams || []), newExam] }))
		setExamDraft({ name: "", subjects: [] })
		setModalType(null)
		setNotice(`${name} added`)
	}

	const removeExam = (examId) => {
		updateCourse((course) => ({
			...course,
			exams: (course.exams || []).filter((ex) => ex.id !== examId),
		}))
		if (selectedExamId === examId) setSelectedExamId("")
	}

	const renameExam = () => {
		const name = editValue.trim()
		if (!name) return
		updateCourse((course) => ({
			...course,
			exams: (course.exams || []).map((ex) => (ex.id === editId ? { ...ex, name } : ex)),
		}))
		cancelEdit()
	}

	const setExamScore = (studentId, subjectId, value) => {
		updateCourse((course) => ({
			...course,
			exams: (course.exams || []).map((ex) =>
				ex.id === selectedExamId
					? {
							...ex,
							scores: {
								...ex.scores,
								[studentId]: {
									...(ex.scores?.[studentId] || {}),
									[subjectId]: value === "" ? "" : Number(value),
								},
							},
					  }
					: ex
			),
		}))
	}

	/* ── Which panel is on screen ── */
	const level = selectedExam
		? "exam"
		: selectedCourse
		? "students"
		: selectedClass
		? "courses"
		: "classes"

	const goToClasses = () => {
		setSelectedClassId("")
		setSelectedCourseId("")
		setSelectedStudentId("")
		setSelectedExamId("")
		cancelEdit()
	}
	const goToCourses = () => {
		setSelectedCourseId("")
		setSelectedStudentId("")
		setSelectedExamId("")
		cancelEdit()
	}
	const goToStudents = () => {
		setSelectedStudentId("")
		setSelectedExamId("")
		cancelEdit()
	}

	return (
		<div className="admin-shell">
			<aside className="admin-sidebar">
				<div className="admin-brand">
					<span className="admin-brand-mark">
						<i className="ri-book-open-line" aria-hidden="true"></i>
					</span>
					<span className="admin-brand-text">
						<strong>Mark Register</strong>
						<small>Admin console</small>
					</span>
				</div>

				<nav className="admin-nav" aria-label="Classes">
					<p className="admin-nav-label">Classes</p>
					<ul>
						{classes.length === 0 && <li className="admin-nav-empty">No classes yet</li>}
						{classes.map((cls) => (
							<li key={cls.id}>
								<button
									type="button"
									className={`admin-nav-item ${cls.id === selectedClassId ? "is-active" : ""}`}
									onClick={() => selectClass(cls.id)}
								>
									<i className="ri-graduation-cap-line" aria-hidden="true"></i>
									<span>{cls.name}</span>
									<em>{cls.courses.length}</em>
								</button>
							</li>
						))}
					</ul>
				</nav>

				<button type="button" className="admin-nav-add-btn" onClick={() => openModal("class")}>
					<i className="ri-add-line" aria-hidden="true"></i> Add class
				</button>

				<div className={`admin-sync is-${syncStatus}`}>
					<i
						className={
							syncStatus === "saving"
								? "ri-loader-4-line"
								: syncStatus === "error"
								? "ri-cloud-off-line"
								: syncStatus === "loading"
								? "ri-loader-4-line"
								: "ri-cloud-line"
						}
						aria-hidden="true"
					></i>
					<span>
						{syncStatus === "loading"
							? "Loading…"
							: syncStatus === "saving"
							? "Saving…"
							: syncStatus === "error"
							? "Offline — not saved"
							: "Saved to cloud"}
					</span>
				</div>
			</aside>

			<main className="admin-main">
				{/* ── Classes dashboard: tiles ── */}
				{level === "classes" && (
					<>
						<header className="admin-head">
							<div className="admin-head-copy">
								<h1 className="admin-title">Dashboard</h1>
								<p className="admin-sub">
									Every class in the mark register. Open a class to manage its courses, students
									and marks.
								</p>
							</div>
							<button
								type="button"
								className="admin-add-btn"
								onClick={() => openModal("class")}
							>
								<i className="ri-add-line" aria-hidden="true"></i> Add class
							</button>
						</header>

						{classes.length === 0 ? (
							<div className="admin-empty">
								<span className="admin-empty-icon">
									<i className="ri-inbox-line" aria-hidden="true"></i>
								</span>
								<h2>No classes yet</h2>
								<p>Add your first class to begin building the mark register.</p>
							</div>
						) : (
							<div className="admin-tiles">
								{classes.map((cls) => {
									const students = cls.courses.reduce(
										(sum, course) => sum + course.students.length,
										0
									)
									return (
										<button
											key={cls.id}
											type="button"
											className="admin-tile"
											onClick={() => selectClass(cls.id)}
										>
											<span className="admin-tile-icon">
												<i className="ri-graduation-cap-line" aria-hidden="true"></i>
											</span>
											<span className="admin-tile-name">{cls.name}</span>
											<span className="admin-tile-meta">
												<span>
													<strong>{cls.courses.length}</strong> courses
												</span>
												<span>
													<strong>{students}</strong> students
												</span>
											</span>
											<span className="admin-tile-open">
												Open <i className="ri-arrow-right-line" aria-hidden="true"></i>
											</span>
										</button>
									)
								})}
							</div>
						)}
					</>
				)}

				{/* ── Courses under a class ── */}
				{level === "courses" && (
					<>
						<header className="admin-head">
							<div className="admin-head-copy">
								<nav className="admin-crumbs">
									<button type="button" onClick={goToClasses}>
										Classes
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<span>{selectedClass.name}</span>
								</nav>
								<h1 className="admin-title">{selectedClass.name}</h1>
								<p className="admin-sub">
									Courses offered under this class. Open a course to manage its students and marks.
								</p>
							</div>
							<button type="button" className="admin-add-btn" onClick={() => openModal("course")}>
								<i className="ri-add-line" aria-hidden="true"></i> Add course
							</button>
						</header>

						<div className="admin-list">
							{selectedClass.courses.length === 0 && (
								<p className="admin-list-empty">No courses under {selectedClass.name} yet.</p>
							)}
							{selectedClass.courses.map((course) => (
								<AdminRow
									key={course.id}
									icon="ri-book-2-line"
									title={course.name}
									meta={`${course.students.length} students`}
									isEditing={editId === course.id}
									editValue={editValue}
									onOpen={() => selectCourse(course.id)}
									onEditStart={() => {
										setEditId(course.id)
										setEditValue(course.name)
									}}
									onEditChange={setEditValue}
									onEditSave={renameCourse}
									onEditCancel={cancelEdit}
									onDelete={() => removeCourse(course.id)}
								/>
							))}
						</div>
					</>
				)}

				{/* ── Students under a course ── */}
				{level === "students" && (
					<>
						<header className="admin-head">
							<div className="admin-head-copy">
								<nav className="admin-crumbs">
									<button type="button" onClick={goToClasses}>
										Classes
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<button type="button" onClick={goToCourses}>
										{selectedClass.name}
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<span>{selectedCourse.name}</span>
								</nav>
								<h1 className="admin-title">{selectedCourse.name}</h1>
								<p className="admin-sub">
									Students enrolled in this course. Open a student to record their marks.
								</p>
							</div>
							<button type="button" className="admin-add-btn" onClick={() => openModal("student")}>
								<i className="ri-add-line" aria-hidden="true"></i> Add student
							</button>
						</header>

						<section className="admin-panel">
							<div className="admin-panel-head">
								<h3>
									Subjects <span className="admin-count">{subjects.length}</span>
								</h3>
								<button
									type="button"
									className="admin-btn is-primary"
									onClick={() => openModal("subject")}
								>
									<i className="ri-add-line" aria-hidden="true"></i> Add subject
								</button>
							</div>
							{subjects.length === 0 ? (
								<p className="admin-panel-empty">
									No subjects yet. Add subjects here — they apply to every student in this course.
								</p>
							) : (
								<div className="admin-chips">
									{subjects.map((subj) => (
										<span key={subj.id} className="admin-chip">
											<i className="ri-book-mark-line" aria-hidden="true"></i>
											<span className="admin-chip-name">{subj.name}</span>
											<span className="admin-chip-total">/ {subj.total}</span>
											<button
												type="button"
												className="admin-chip-x"
												onClick={() => removeSubject(subj.id)}
												aria-label={`Remove ${subj.name}`}
											>
												<i className="ri-close-line" aria-hidden="true"></i>
											</button>
										</span>
									))}
								</div>
							)}
						</section>

						<div className="admin-section-bar">
							<h3 className="admin-section-label">
								Exams <span className="admin-count">{(selectedCourse.exams || []).length}</span>
							</h3>
							<button
								type="button"
								className="admin-btn is-primary"
								onClick={() => openModal("exam")}
							>
								<i className="ri-add-line" aria-hidden="true"></i> Add exam
							</button>
						</div>
						<div className="admin-list">
							{(selectedCourse.exams || []).length === 0 && (
								<p className="admin-list-empty">
									No exams yet. Add an exam and its subjects, then open it to enter marks.
								</p>
							)}
							{(selectedCourse.exams || []).map((exam) => (
								<AdminRow
									key={exam.id}
									icon="ri-file-list-3-line"
									title={exam.name}
									meta={`${exam.subjects.length} subjects · ${selectedCourse.students.length} students`}
									isEditing={editId === exam.id}
									editValue={editValue}
									onOpen={() => selectExam(exam.id)}
									onEditStart={() => {
										setEditId(exam.id)
										setEditValue(exam.name)
									}}
									onEditChange={setEditValue}
									onEditSave={renameExam}
									onEditCancel={cancelEdit}
									onDelete={() => removeExam(exam.id)}
								/>
							))}
						</div>

						<h3 className="admin-section-label">
							Students <span className="admin-count">{selectedCourse.students.length}</span>
						</h3>
						<div className="admin-list">
							{selectedCourse.students.length === 0 && (
								<p className="admin-list-empty">No students under {selectedCourse.name} yet.</p>
							)}
							{selectedCourse.students.map((student, index) => (
								<AdminRow
									key={student.id}
									icon="ri-user-line"
									title={student.name}
									meta={`Roll ${student.rollNo || pad(index + 1)}`}
									isEditing={editId === student.id}
									editValue={editValue}
									onEditStart={() => {
										setEditId(student.id)
										setEditValue(student.name)
									}}
									onEditChange={setEditValue}
									onEditSave={renameStudent}
									onEditCancel={cancelEdit}
									onDelete={() => removeStudent(student.id)}
								/>
							))}
						</div>
					</>
				)}

				{/* ── Exam: enter marks for every student ── */}
				{level === "exam" && (
					<>
						<header className="admin-head">
							<div className="admin-head-copy">
								<nav className="admin-crumbs">
									<button type="button" onClick={goToClasses}>
										Classes
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<button type="button" onClick={goToCourses}>
										{selectedClass.name}
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<button type="button" onClick={goToStudents}>
										{selectedCourse.name}
									</button>
									<i className="ri-arrow-right-s-line" aria-hidden="true"></i>
									<span>{selectedExam.name}</span>
								</nav>
								<h1 className="admin-title">{selectedExam.name}</h1>
								<p className="admin-sub">
									Enter each student's marks. {selectedExam.subjects.length} subjects ·{" "}
									{selectedCourse.students.length} students.
								</p>
							</div>
						</header>

						{selectedExam.subjects.length === 0 || selectedCourse.students.length === 0 ? (
							<div className="admin-empty">
								<span className="admin-empty-icon">
									<i className="ri-file-list-3-line" aria-hidden="true"></i>
								</span>
								<h2>Nothing to mark yet</h2>
								<p>
									{selectedExam.subjects.length === 0
										? "This exam has no subjects. Delete it and add one with subjects."
										: "Add students to this course first, then enter their marks."}
								</p>
								<button type="button" className="admin-add-btn" onClick={goToStudents}>
									<i className="ri-arrow-left-line" aria-hidden="true"></i> Back to course
								</button>
							</div>
						) : (
							<div className="admin-sheet">
								<table className="admin-table admin-exam-table">
									<thead>
										<tr>
											<th className="admin-col-no">Roll</th>
											<th className="admin-col-student">Student</th>
											{selectedExam.subjects.map((subj) => (
												<th key={subj.id} className="admin-col-num">
													{subj.name}
													<span className="admin-th-total">/ {subj.total}</span>
												</th>
											))}
											<th className="admin-col-num">Total</th>
											<th className="admin-col-verdict">%</th>
											<th className="admin-col-verdict">Result</th>
										</tr>
									</thead>
									<tbody>
										{selectedCourse.students.map((student, index) => {
											const t = examTotals(selectedExam, student.id)
											const passed = t.percentage >= 35
											return (
												<tr key={student.id}>
													<td className="admin-col-no">{student.rollNo || pad(index + 1)}</td>
													<td className="admin-subject-name">{student.name}</td>
													{selectedExam.subjects.map((subj) => {
														const raw = selectedExam.scores?.[student.id]?.[subj.id]
														return (
															<td key={subj.id}>
																<input
																	type="number"
																	min="0"
																	max={subj.total}
																	className="admin-cell admin-cell-num"
																	placeholder="0"
																	value={raw === undefined ? "" : raw}
																	aria-label={`${student.name} — ${subj.name}`}
																	onChange={(e) => setExamScore(student.id, subj.id, e.target.value)}
																/>
															</td>
														)
													})}
													<td className="admin-foot-num">
														{t.obtained}
														<span className="admin-out-of"> / {t.maximum}</span>
													</td>
													<td className="admin-out-of">{t.percentage.toFixed(1)}</td>
													<td>
														<span className={`admin-pill is-${passed ? "pass" : "fail"}`}>
															{passed ? "Pass" : "Fail"}
														</span>
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</main>

			{modalType && (
				<div
					className="admin-modal-backdrop"
					role="dialog"
					aria-modal="true"
					aria-label={`Add ${modalType}`}
					onMouseDown={(e) => {
						if (e.target === e.currentTarget) closeModal()
					}}
				>
					<div className="admin-modal">
						{modalType === "class" && (
							<form onSubmit={addClass}>
								<div className="admin-modal-head">
									<h2>Add class</h2>
									<button
										type="button"
										className="admin-modal-close"
										onClick={closeModal}
										aria-label="Close"
									>
										<i className="ri-close-line" aria-hidden="true"></i>
									</button>
								</div>
								<p className="admin-modal-sub">
									Create a new class. You can add courses, students and marks to it afterwards.
								</p>
								<label className="admin-modal-label" htmlFor="modal-class-name">
									Class name
								</label>
								<input
									id="modal-class-name"
									type="text"
									autoFocus
									className="admin-modal-input"
									placeholder="e.g. Plus One"
									value={classInput}
									onChange={(e) => setClassInput(e.target.value)}
								/>
								<div className="admin-modal-actions">
									<button type="button" className="admin-btn" onClick={closeModal}>
										Cancel
									</button>
									<button type="submit" className="admin-add-btn" disabled={!classInput.trim()}>
										<i className="ri-add-line" aria-hidden="true"></i> Add class
									</button>
								</div>
							</form>
						)}

						{modalType === "course" && (
							<form onSubmit={addCourse}>
								<div className="admin-modal-head">
									<h2>Add course</h2>
									<button
										type="button"
										className="admin-modal-close"
										onClick={closeModal}
										aria-label="Close"
									>
										<i className="ri-close-line" aria-hidden="true"></i>
									</button>
								</div>
								<p className="admin-modal-sub">
									Add a course under <strong>{selectedClass?.name}</strong>.
								</p>
								<label className="admin-modal-label" htmlFor="modal-course-name">
									Course name
								</label>
								<input
									id="modal-course-name"
									type="text"
									autoFocus
									className="admin-modal-input"
									placeholder="e.g. Science"
									value={courseInput}
									onChange={(e) => setCourseInput(e.target.value)}
								/>
								<div className="admin-modal-actions">
									<button type="button" className="admin-btn" onClick={closeModal}>
										Cancel
									</button>
									<button type="submit" className="admin-add-btn" disabled={!courseInput.trim()}>
										<i className="ri-add-line" aria-hidden="true"></i> Add course
									</button>
								</div>
							</form>
						)}

						{modalType === "student" && (
							<form onSubmit={addStudent}>
								<div className="admin-modal-head">
									<h2>Add student</h2>
									<button
										type="button"
										className="admin-modal-close"
										onClick={closeModal}
										aria-label="Close"
									>
										<i className="ri-close-line" aria-hidden="true"></i>
									</button>
								</div>
								<p className="admin-modal-sub">
									Add a student to <strong>{selectedCourse?.name}</strong>.
								</p>
								<label className="admin-modal-label" htmlFor="modal-student-name">
									Student name
								</label>
								<input
									id="modal-student-name"
									type="text"
									autoFocus
									className="admin-modal-input"
									placeholder="e.g. Aravind K"
									value={studentInput.name}
									onChange={(e) =>
										setStudentInput((prev) => ({ ...prev, name: e.target.value }))
									}
								/>
								<label className="admin-modal-label" htmlFor="modal-student-roll">
									Roll number <span className="admin-modal-optional">(optional)</span>
								</label>
								<input
									id="modal-student-roll"
									type="text"
									className="admin-modal-input"
									placeholder="e.g. 12"
									value={studentInput.rollNo}
									onChange={(e) =>
										setStudentInput((prev) => ({ ...prev, rollNo: e.target.value }))
									}
								/>
								<div className="admin-modal-actions">
									<button type="button" className="admin-btn" onClick={closeModal}>
										Cancel
									</button>
									<button
										type="submit"
										className="admin-add-btn"
										disabled={!studentInput.name.trim()}
									>
										<i className="ri-add-line" aria-hidden="true"></i> Add student
									</button>
								</div>
							</form>
						)}

						{modalType === "subject" && (
							<form onSubmit={addSubject}>
								<div className="admin-modal-head">
									<h2>Add subject</h2>
									<button
										type="button"
										className="admin-modal-close"
										onClick={closeModal}
										aria-label="Close"
									>
										<i className="ri-close-line" aria-hidden="true"></i>
									</button>
								</div>
								<p className="admin-modal-sub">
									Add a subject to <strong>{selectedCourse?.name}</strong>. It applies to every
									student in this course.
								</p>
								<label className="admin-modal-label" htmlFor="modal-subject-name">
									Subject name
								</label>
								<input
									id="modal-subject-name"
									type="text"
									autoFocus
									className="admin-modal-input"
									placeholder="e.g. Physics"
									value={subjectDraft.name}
									onChange={(e) => setSubjectDraft((prev) => ({ ...prev, name: e.target.value }))}
								/>
								<div className="admin-modal-actions">
									<button type="button" className="admin-btn" onClick={closeModal}>
										Cancel
									</button>
									<button type="submit" className="admin-add-btn" disabled={!subjectDraft.name.trim()}>
										<i className="ri-add-line" aria-hidden="true"></i> Add subject
									</button>
								</div>
							</form>
						)}

						{modalType === "exam" && (
							<form onSubmit={addExam} className="admin-modal-exam">
								<div className="admin-modal-head">
									<h2>Add exam</h2>
									<button
										type="button"
										className="admin-modal-close"
										onClick={closeModal}
										aria-label="Close"
									>
										<i className="ri-close-line" aria-hidden="true"></i>
									</button>
								</div>
								<p className="admin-modal-sub">
									Name the exam and set its subjects. Marks are entered per student afterwards.
								</p>
								<label className="admin-modal-label" htmlFor="modal-exam-name">
									Exam name
								</label>
								<input
									id="modal-exam-name"
									type="text"
									autoFocus
									className="admin-modal-input"
									placeholder="e.g. First Terminal"
									value={examDraft.name}
									onChange={(e) => setExamDraft((prev) => ({ ...prev, name: e.target.value }))}
								/>

								<div className="admin-modal-label admin-exam-sub-head">
									<span>Subjects</span>
									<button type="button" className="admin-mini-add" onClick={addExamSubjectRow}>
										<i className="ri-add-line" aria-hidden="true"></i> Add subject
									</button>
								</div>

								{examDraft.subjects.length === 0 ? (
									<p className="admin-panel-empty">No subjects added. Click “Add subject”.</p>
								) : (
									<div className="admin-exam-rows">
										{examDraft.subjects.map((row) => (
											<div className="admin-exam-row" key={row.id}>
												<input
													type="text"
													className="admin-modal-input"
													placeholder="Subject name"
													aria-label="Subject name"
													value={row.name}
													onChange={(e) => updateExamSubjectRow(row.id, "name", e.target.value)}
												/>
												<input
													type="number"
													min="1"
													className="admin-modal-input admin-exam-total"
													placeholder="100"
													aria-label="Out of"
													value={row.total}
													onChange={(e) => updateExamSubjectRow(row.id, "total", e.target.value)}
												/>
												<button
													type="button"
													className="admin-chip-x"
													onClick={() => removeExamSubjectRow(row.id)}
													aria-label="Remove subject"
												>
													<i className="ri-close-line" aria-hidden="true"></i>
												</button>
											</div>
										))}
									</div>
								)}

								<div className="admin-modal-actions">
									<button type="button" className="admin-btn" onClick={closeModal}>
										Cancel
									</button>
									<button type="submit" className="admin-add-btn" disabled={!examDraft.name.trim()}>
										<i className="ri-add-line" aria-hidden="true"></i> Add exam
									</button>
								</div>
							</form>
						)}
					</div>
				</div>
			)}

			<div className="admin-notice-slot" role="status" aria-live="polite">
				{notice && <p className="admin-notice">{notice}</p>}
			</div>
		</div>
	)
}

export default MarkListEnter
